/* eslint-disable jsx-a11y/tabindex-no-positive */
import React, { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
} from 'reactstrap';
import { toast } from 'react-toastify';

import PageHeader from '~/components/Common/PageHeader';
import Input from '~/components/Inputs/Input';
import InputMask from '~/components/Inputs/InputMask';
import InputSelect from '~/components/Inputs/InputSelect';

import { maskBuilder } from '~/util/maskTelephone';
import history from '~/services/history';
import api from '~/services/api';

const PARTNER_MAP = {
  BEE: 'Bee Delivery',
  UBER: 'Uber',
  MOTTU: 'Mottu',
  LALAMOVE: 'Lalamove',
  VIDDY_DELIVERIES: 'Viddy Entregas',
  ENTREGA99: '99 Entregas',
  IFOOD_ENVIOS: 'iFood Envios',
  PROPRIA: 'Entrega Própria',
};

const normalizeCode = code =>
  String(code || '')
    .trim()
    .toUpperCase();
const prettyFromCode = code => {
  const c = normalizeCode(code);
  return (
    PARTNER_MAP[c] ||
    c.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
  );
};
const toOption = code => ({
  value: normalizeCode(code),
  label: prettyFromCode(code),
});

const VEHICLE_TYPES = [
  { value: 'Moto', label: 'Moto' },
  { value: 'Carro', label: 'Carro' },
  { value: 'Bicicleta', label: 'Bicicleta' },
];

let partnersCache = null;
let partnersPromise = null;

function formatBRPhone(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length === 11) {
    // (11) 98122-3788
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
  }
  if (d.length === 10) {
    // (11) 3122-3788
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6, 10)}`;
  }
  return null; // deixa o backend ignorar se vier inválido
}

const NewBlockedCourier = () => {
  const formRef = useRef(null);

  const [telephoneV, setTelephone] = useState('');
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Cadastrar Entregador Bloqueado | Viddy';
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setPartnersLoading(true);
      try {
        if (partnersCache && mounted) {
          setPartners(partnersCache);
          return;
        }

        if (!partnersPromise) {
          partnersPromise = api
            .get('/utils/available-delivery-services')
            .then(({ data }) => {
              const arr = Array.isArray(data?.delivery_services)
                ? data.delivery_services
                : [];
              const seen = new Set();
              const options = arr
                .map(code => toOption(code))
                .filter(opt => {
                  if (seen.has(opt.value)) return false;
                  seen.add(opt.value);
                  return true;
                })
                .sort((a, b) => a.label.localeCompare(b.label));

              const finalOpts =
                options.length > 0
                  ? options
                  : [toOption('BEE'), toOption('UBER')];

              partnersCache = finalOpts;
              return finalOpts;
            })
            .catch(() => {
              const fallback = [toOption('BEE'), toOption('UBER')];
              partnersCache = fallback;
              return fallback;
            })
            .finally(() => {
              partnersPromise = null;
            });
        }

        const result = await partnersPromise;
        if (mounted) setPartners(result);
      } finally {
        if (mounted) setPartnersLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async formData => {
    try {
      formRef.current?.setErrors({});

      const deliveryMethod =
        normalizeCode(
          formData?.delivery_method?.value ||
            formData?.delivery_method ||
            selectedPartner?.value
        ) || null;

      if (!deliveryMethod) {
        toast.error('Selecione o parceiro de entrega.');
        return;
      }

      if (!formData?.name || !String(formData.name).trim()) {
        toast.error('Informe o nome do entregador.');
        return;
      }

      const vehicleType =
        formData?.vehicle_type?.value ||
        formData?.vehicle_type ||
        selectedVehicleType?.value ||
        undefined;

      setSubmitting(true);

      const payload = {
        name: String(formData.name).trim(),
        telephone: formData.telephone
          ? String(formatBRPhone(formData.telephone)).replace(/\D/g, '')
          : undefined,
        delivery_method: deliveryMethod,
        courier_external_id: formData.courier_external_id
          ? String(formData.courier_external_id).trim()
          : undefined,
        vehicle_plate: formData.vehicle_plate
          ? String(formData.vehicle_plate).trim().toUpperCase()
          : undefined,
        vehicle_make: formData.vehicle_make
          ? String(formData.vehicle_make).trim()
          : undefined,
        vehicle_model: formData.vehicle_model
          ? String(formData.vehicle_model).trim()
          : undefined,
        vehicle_color: formData.vehicle_color
          ? String(formData.vehicle_color).trim()
          : undefined,
        vehicle_type: vehicleType,
      };

      Object.keys(payload).forEach(
        k => payload[k] === undefined && delete payload[k]
      );

      await api.post('/administrator/blacklist', payload);

      toast.success('Entregador cadastrado com sucesso!');
      history.push('/entregadores-bloqueados');
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error(err.message || 'Erro ao cadastrar entregador.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="Cadastrar Entregador"
          backTo="/entregadores-bloqueados"
          responsivePosition="start"
          desktopPosition="center"
        />

        <Form
          id="courier-form"
          ref={formRef}
          onSubmit={handleSubmit}
          noValidate
        >
          <Card className="card new-layout">
            <CardBody className="card-body new-layout">
              <Row className="w-100 mx-0 font-size-20 font-weight-700 text-gray-700 my-4">
                Dados do Entregador
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <Label>Transportadora</Label>
                  <FormGroup>
                    <InputSelect
                      name="delivery_method"
                      placeholder={
                        partnersLoading
                          ? 'Carregando...'
                          : 'Selecione um parceiro'
                      }
                      options={partners}
                      value={selectedPartner}
                      onChange={setSelectedPartner}
                      isDisabled={partnersLoading || partners.length === 0}
                      singlePath
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <Label>ID Externo</Label>
                  <FormGroup>
                    <Input
                      name="courier_external_id"
                      placeholder="ID externo do parceiro"
                      type="text"
                      tabIndex={2}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <Label>Nome</Label>
                  <FormGroup>
                    <Input
                      name="name"
                      placeholder="Nome do entregador"
                      type="text"
                      tabIndex={3}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <Label>Telefone</Label>
                  <FormGroup>
                    <InputMask
                      mask={maskBuilder(telephoneV)}
                      maskChar={null}
                      defaultValue={telephoneV}
                      name="telephone"
                      placeholder="Telefone"
                      type="text"
                      onChange={e => setTelephone(e.target.value)}
                      tabIndex={4}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0 font-size-20 font-weight-700 text-gray-700 my-4">
                Dados do Veículo
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={4} className="px-0 pr-lg-2">
                  <Label>Placa</Label>
                  <FormGroup>
                    <Input
                      name="vehicle_plate"
                      placeholder="Placa do veículo"
                      type="text"
                      tabIndex={5}
                    />
                  </FormGroup>
                </Col>
                <Col lg={4} className="px-0 px-lg-2">
                  <Label>Marca</Label>
                  <FormGroup>
                    <Input
                      name="vehicle_make"
                      placeholder="Marca"
                      type="text"
                      tabIndex={6}
                    />
                  </FormGroup>
                </Col>
                <Col lg={4} className="px-0 pl-lg-2">
                  <Label>Modelo</Label>
                  <FormGroup>
                    <Input
                      name="vehicle_model"
                      placeholder="Modelo"
                      type="text"
                      tabIndex={7}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <Label>Cor</Label>
                  <FormGroup>
                    <Input
                      name="vehicle_color"
                      placeholder="Cor"
                      type="text"
                      tabIndex={8}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <Label>Tipo</Label>
                  <FormGroup>
                    <InputSelect
                      name="vehicle_type"
                      placeholder="Selecione o tipo"
                      options={VEHICLE_TYPES}
                      value={selectedVehicleType}
                      onChange={setSelectedVehicleType}
                      singlePath
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row className="w-100 mx-0 my-4">
            <Col align="end" className="px-0">
              <Button
                className="new-layout-btn main"
                color="primary"
                size="lg"
                type="submit"
                tabIndex={9}
                disabled={submitting}
              >
                {submitting ? 'Salvando...' : 'Salvar alterações'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default NewBlockedCourier;
